import './ContactForm.scss';
import React, { useState, useEffect } from 'react';
import { X } from '@phosphor-icons/react';
import { useContactsAppStore } from '../../store/contactsAppStore';
import { ContactTier } from '../../types/entity';

interface ContactFormProps {
  windowId: string;
  spaceId: string;
  contactId?: string;
  onClose: () => void;
}

interface FormState {
  name: string;
  email: string;
  phone: string;
  company: string;
  tier: ContactTier;
  notes: string;
}

const DEFAULT_FORM: FormState = {
  name: '',
  email: '',
  phone: '',
  company: '',
  tier: 'regular',
  notes: '',
};

export const ContactForm: React.FC<ContactFormProps> = ({ windowId, spaceId, contactId, onClose }) => {
  const store = useContactsAppStore(windowId, spaceId);
  const contacts = store((s) => s.contacts);
  const createContact = store((s) => s.createContact);
  const updateContact = store((s) => s.updateContact);
  const selectContact = store((s) => s.selectContact);

  const isEditing = Boolean(contactId);
  const existing = contactId ? contacts.find((c) => c._id === contactId) : undefined;

  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (existing) {
      const sk = existing.skeleton;
      setForm({
        name: existing.name,
        email: sk.email || '',
        phone: sk.phone || '',
        company: sk.company || '',
        tier: sk.tier,
        notes: sk.notes || '',
      });
    }
  }, [contactId]);

  const set = (field: keyof FormState) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      if (isEditing && contactId) {
        await updateContact(contactId, {
          name: form.name.trim(),
          email: form.email,
          phone: form.phone,
          company: form.company,
          tier: form.tier,
          notes: form.notes,
        });
        onClose();
      } else {
        const created = await createContact({
          name: form.name.trim(),
          email: form.email || undefined,
          phone: form.phone || undefined,
          company: form.company || undefined,
          tier: form.tier,
          notes: form.notes || undefined,
        });
        if (created) {
          selectContact(created._id);
        }
        onClose();
      }
    } catch {
      setError('Failed to save contact. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="contact-form-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="contact-form">
        <div className="contact-form__header">
          <h2>{isEditing ? 'Edit Contact' : 'New Contact'}</h2>
          <button className="contact-form__close" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <form className="contact-form__body" onSubmit={handleSubmit}>
          {/* Basic info */}
          <div className="contact-form__section">
            <div className="contact-form__field">
              <label>Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={set('name')}
                placeholder="Full name"
                autoFocus
              />
            </div>
            <div className="contact-form__row">
              <div className="contact-form__field">
                <label>Email</label>
                <input type="email" value={form.email} onChange={set('email')} placeholder="email@example.com" />
              </div>
              <div className="contact-form__field">
                <label>Phone</label>
                <input type="tel" value={form.phone} onChange={set('phone')} placeholder="+1 555 000 0000" />
              </div>
            </div>
            <div className="contact-form__field">
              <label>Company</label>
              <input type="text" value={form.company} onChange={set('company')} placeholder="Company or org" />
            </div>
          </div>

          {/* Notes */}
          <div className="contact-form__section">
            <div className="contact-form__field">
              <label>Notes</label>
              <textarea
                value={form.notes}
                onChange={set('notes')}
                placeholder="Context, background, how you met…"
                rows={3}
              />
            </div>
          </div>

          {error && <p className="contact-form__error">{error}</p>}

          <div className="contact-form__footer">
            <button type="button" className="contact-form__btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="contact-form__btn contact-form__btn--primary"
              disabled={isSaving}
            >
              {isSaving ? 'Saving…' : isEditing ? 'Save Changes' : 'Create Contact'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
